// ============================================================================
// Compliance decisions — one implementation, every surface
// ============================================================================
// Approving, rejecting, requesting more information and replying each touch
// five things: the document row, the conversation thread, the event spine, the
// audit log, and the supplier's inbox. Getting one of those wrong is how a
// supplier ends up rejected with no reason recorded, or approved with nothing
// in the history.
//
// The review page and the document detail page both offer these decisions
// (detail-page brief §6). They must behave identically — same writes, same
// emails, same audit rows — so the logic lives here and the pages own only
// their own chrome: the review page keeps its queue auto-advance, expiry
// confirmation and eco-badge recalculation; the detail page keeps its stream.
//
// Every function returns { error, emailed, noAddress, ... } rather than
// throwing: the DB write has usually already happened by the time an email
// fails, and callers need to say so precisely instead of showing "failed".
// ============================================================================

import { supabase } from '../supabaseClient';
import { logComplianceEvent } from './complianceEvents';
import {
  sendEmail,
  complianceDocumentApprovedEmail,
  complianceClarificationRequestedEmail,
  complianceDocumentRejectedEmail,
  complianceReplyEmail,
  complianceNudgeEmail,
} from './email';
import { formatDateBritish } from './compliance';

// --- the reason lists both surfaces offer -----------------------------------
// Defaults only: Admin can configure the snippets, and older rows stored the
// KEY rather than the label, which is why reasonLabel still decodes both.

export const REQUEST_INFO_REASONS = [
  { key: 'confirm_coverage',    label: 'Confirm coverage details' },
  { key: 'confirm_expiry',      label: 'Confirm expiry date is correct' },
  { key: 'clarify_scope',       label: 'Clarify scope' },
  { key: 'confirm_signatories', label: 'Confirm signatories' },
  { key: 'other',               label: 'Other' },
];

export const REJECT_REASONS = [
  { key: 'document_expired',      label: 'Document expired' },
  { key: 'wrong_type',            label: 'Wrong document type' },
  { key: 'insufficient_coverage', label: 'Insufficient coverage' },
  { key: 'illegible',             label: 'Illegible / poor quality' },
  { key: 'missing_signatures',    label: 'Missing signatures or pages' },
  { key: 'other',                 label: 'Other' },
];

// A reason without an explanation is not a reason — the supplier reads these
// verbatim and has to act on them.
export const MIN_NOTES_LENGTH = 10;

export const reasonLabel = (key, options) => options.find(o => o.key === key)?.label || key;

// --- shared plumbing --------------------------------------------------------

// Recipients are the supplier's ACTIVE portal users — not the commercial
// contact. A supplier with none is reported (noAddress) rather than failing
// silently, because the reviewer needs to know nobody was told.
async function emailSupplier({ supplierId, supplierName, buildFor, emailKind, actorId }) {
  if (!supplierId) return { emailed: 0, noAddress: true };
  const { data: users } = await supabase
    .from('supplier_users')
    .select('email, full_name')
    .eq('supplier_id', supplierId)
    .eq('is_active', true);
  const recipients = (users || []).filter(u => u.email);

  const sentTo = [];
  for (const u of recipients) {
    const payload = buildFor(u, supplierName);
    if (!payload) continue;
    try {
      await sendEmail({ to: u.email, subject: payload.subject, html: payload.html });
      sentTo.push(u.email);
    } catch {
      // The decision is already recorded; a failed send must not undo it.
    }
  }
  if (sentTo.length > 0 && emailKind) {
    await supabase.from('supplier_email_log').insert([{
      supplier_id: supplierId, email_kind: emailKind, recipients: sentTo, sent_by: actorId || null,
    }]).then(() => {}, () => {});
  }
  return { emailed: sentTo.length, noAddress: recipients.length === 0 };
}

// The reviewer's words go into the thread the supplier reads, so a decision is
// never a bare status change. Body must be non-empty (table CHECK).
//
// THIS USED TO SWALLOW ITS ERROR, which broke the invariant in the line above
// without telling anybody. On a rejection or a clarification request the text
// IS the payload — the document flipped to "we need more information" and the
// supplier got a thread with nothing in it saying what was wrong, while the
// reviewer saw a success toast. Now it reports, and each caller decides how
// much that costs: fatal where the words are the point, a warning where the
// comment was optional. (Same smell as the confirmation-notifications sweep:
// unchecked mutations, not missing toasts.)
async function appendAdminMessage(supplierId, documentKey, body, actorId) {
  const text = (body || '').trim();
  if (!supplierId || !documentKey || !text) return { error: null };
  const { error } = await supabase.from('compliance_clarification_messages').insert({
    supplier_id: supplierId,
    document_key: documentKey,
    author: 'admin',
    body: text,
    created_by: actorId || null,
  });
  return { error: error ? error.message : null };
}

const auditRow = (action, doc, actorId, meta) => ({
  actor_id: actorId || null,
  action,
  supplier_id: doc.supplier_id,
  document_key: doc.document_key,
  meta,
});

// --- decisions --------------------------------------------------------------

// Approve. `confirmedExpiry` is the reviewer's confirmation of the supplier's
// date — approving CONFIRMS an expiry, so a correction is recorded as its own
// auditable override rather than folded silently into the approval.
export async function approveDocument({
  doc, supplier, documentLabel, confirmedExpiry, comment, actorId, source = 'review_page',
}) {
  if (!doc) return { error: 'No document' };
  const now = new Date().toISOString();
  const enteredExpiry = doc.expiry_date || null;
  const finalExpiry = confirmedExpiry !== undefined ? (confirmedExpiry || null) : enteredExpiry;
  const expiryChanged = finalExpiry !== enteredExpiry;

  const { error } = await supabase
    .from('supplier_onboarding_documents')
    .update({
      // ⚠️ NOT 'approved'. review_decision only holds a PROBLEM — NULL,
      // 'rejected' or 'needs_clarification' (see the CHECK on the table).
      // An approval is expiry_approved_at being set with no outstanding
      // decision; writing 'approved' here violates the constraint and the
      // approval fails outright.
      review_decision: null,
      review_reason: null,
      review_notes: null,
      reviewed_at: now,
      reviewed_by: actorId || null,
      expiry_date: finalExpiry,
      expiry_approved_at: now,
      expiry_approved_by: actorId || null,
      // A renewed/approved cycle starts the reminder clock again.
      expiry_email_sent_at: null,
      expiry_warning_email_sent_at: null,
      updated_at: now,
    })
    .eq('id', doc.id);
  if (error) return { error: error.message };

  // NOT fatal here. The comment on an approval is optional and additive — the
  // document is approved either way, and failing the whole approval because a
  // pleasantry didn't save would be the wrong trade. Carried back as a warning
  // so the reviewer knows the supplier won't see it.
  let commentWarning = null;
  if ((comment || '').trim()) {
    const msg = await appendAdminMessage(doc.supplier_id, doc.document_key, comment, actorId);
    if (msg.error) commentWarning = `Approved, but your comment did not reach the supplier (${msg.error}).`;
  }

  logComplianceEvent({
    supplierId: doc.supplier_id, documentKey: doc.document_key,
    eventType: 'approved', actorSide: 'staff', actorUserId: actorId || null,
    meta: finalExpiry ? { expiry_date: finalExpiry } : {},
  });

  await supabase.rpc('recompute_supplier_compliance_status', { p_supplier_id: doc.supplier_id });

  supabase.from('compliance_audit_log').insert([
    auditRow('approved', doc, actorId, {
      viewedFile: true, source, expiryConfirmed: finalExpiry, expiryEntered: enteredExpiry,
    }),
    // A corrected expiry is its own auditable change, legible in the activity
    // feed rather than buried in the approval's meta.
    ...(expiryChanged ? [auditRow('override', doc, actorId, {
      changedFrom: enteredExpiry, changedTo: finalExpiry, autoApproved: true, source,
    })] : []),
  ]).then(() => {}, () => {});

  const mail = await emailSupplier({
    supplierId: supplier?.id,
    supplierName: supplier?.supplier_name,
    emailKind: 'compliance_approved',
    actorId,
    buildFor: (u, supplierName) => complianceDocumentApprovedEmail({
      contactName: u.full_name, supplierName, documentLabel,
      portalUrl: `${window.location.origin}/portal/compliance`,
    }),
  });

  return { ...mail, expiryChanged, finalExpiry, ...(commentWarning ? { warning: commentWarning } : {}) };
}

// Ask for more information. The document stays with the supplier: any previous
// approval is cleared, and their last reply is wiped so the exchange is fresh.
export async function requestInfoOnDocument({
  doc, supplier, documentLabel, reason, reasonLabel, notes, actorId, source = 'review_page',
}) {
  if (!doc) return { error: 'No document' };
  const now = new Date().toISOString();
  const text = (notes || '').trim();

  const { error } = await supabase
    .from('supplier_onboarding_documents')
    .update({
      review_decision: 'needs_clarification',
      review_reason: reason,
      review_notes: text,
      reviewed_at: now,
      reviewed_by: actorId || null,
      supplier_reply: null,
      supplier_reply_at: null,
      expiry_approved_at: null,
      expiry_approved_by: null,
      updated_at: now,
    })
    .eq('id', doc.id);
  if (error) return { error: error.message };

  // FATAL. "We need more information" with no message is worse than no
  // decision at all — the supplier is blocked and cannot find out why, and
  // the reviewer has no reason to think anything went wrong. The status change
  // is already written, so this reports rather than pretending it rolled back.
  const msg = await appendAdminMessage(doc.supplier_id, doc.document_key, text, actorId);
  if (msg.error) {
    return { error: `The document was moved to "needs more information", but your message did not reach ${supplier?.supplier_name || 'the supplier'} (${msg.error}). Send it again from the document before they see the request.` };
  }
  logComplianceEvent({
    supplierId: doc.supplier_id, documentKey: doc.document_key,
    eventType: 'clarification_requested', actorSide: 'staff', actorUserId: actorId || null,
  });
  await supabase.rpc('recompute_supplier_compliance_status', { p_supplier_id: doc.supplier_id });
  supabase.from('compliance_audit_log')
    .insert([auditRow('needs_clarification', doc, actorId, { reason, notes: text, source })])
    .then(() => {}, () => {});

  return emailSupplier({
    supplierId: supplier?.id,
    supplierName: supplier?.supplier_name,
    emailKind: 'compliance_needs_info',
    actorId,
    buildFor: (u, supplierName) => complianceClarificationRequestedEmail({
      contactName: u.full_name, supplierName, documentLabel, reasonLabel, notes: text,
      portalUrl: `${window.location.origin}/portal/compliance`,
    }),
  });
}

// Reject. Same shape as request-info, but the supplier has to provide a new
// document rather than answer a question.
export async function rejectDocument({
  doc, supplier, documentLabel, reason, reasonLabel, notes, actorId, source = 'review_page',
}) {
  if (!doc) return { error: 'No document' };
  const now = new Date().toISOString();
  const text = (notes || '').trim();

  const { error } = await supabase
    .from('supplier_onboarding_documents')
    .update({
      review_decision: 'rejected',
      review_reason: reason,
      review_notes: text,
      reviewed_at: now,
      reviewed_by: actorId || null,
      expiry_approved_at: null,
      expiry_approved_by: null,
      updated_at: now,
    })
    .eq('id', doc.id);
  if (error) return { error: error.message };

  // FATAL for the same reason as a clarification request: a rejection whose
  // reason never arrives leaves the supplier re-uploading the same document.
  // The rejection email below still carries the notes, so this is recoverable
  // — but the thread is what they read on the page, and it must not diverge
  // from the email silently.
  const msg = await appendAdminMessage(doc.supplier_id, doc.document_key, text, actorId);
  if (msg.error) {
    return { error: `The document was rejected, but your reason did not reach ${supplier?.supplier_name || 'the supplier'} (${msg.error}). Add it again from the document so they know what to fix.` };
  }
  logComplianceEvent({
    supplierId: doc.supplier_id, documentKey: doc.document_key,
    eventType: 'rejected', actorSide: 'staff', actorUserId: actorId || null,
  });
  await supabase.rpc('recompute_supplier_compliance_status', { p_supplier_id: doc.supplier_id });
  supabase.from('compliance_audit_log')
    .insert([auditRow('rejected', doc, actorId, { reason, notes: text, source })])
    .then(() => {}, () => {});

  return emailSupplier({
    supplierId: supplier?.id,
    supplierName: supplier?.supplier_name,
    emailKind: 'compliance_rejected',
    actorId,
    buildFor: (u, supplierName) => complianceDocumentRejectedEmail({
      contactName: u.full_name, supplierName, documentLabel, reasonLabel, notes: text,
      portalUrl: `${window.location.origin}/portal/compliance`,
    }),
  });
}

// Nudge — "you haven't come back to us". Changes nothing about the document:
// it's a reminder that a question is still open, not a decision. Whether it's
// ALLOWED (3 working days, once a week, only while genuinely waiting) is
// decided by lib/complianceNudge; this just sends it and records the send so
// the cooldown has something to read.
export async function nudgeSupplier({ doc, supplier, documentLabel, question, askedAt, actorId }) {
  if (!doc) return { error: 'No document' };

  const mail = await emailSupplier({
    supplierId: supplier?.id,
    supplierName: supplier?.supplier_name,
    emailKind: 'compliance_needs_info',
    actorId,
    buildFor: (u, supplierName) => complianceNudgeEmail({
      contactName: u.full_name,
      supplierName,
      documentLabel,
      question,
      askedOn: askedAt ? formatDateBritish(String(askedAt).slice(0, 10)) : null,
      portalUrl: `${window.location.origin}/portal/compliance`,
    }),
  });

  // Only record a nudge that actually went out — otherwise a failed send would
  // start a 7-day cooldown on a reminder nobody received.
  if (mail.emailed > 0) {
    await supabase.from('compliance_audit_log')
      .insert([auditRow('reminder_sent', doc, actorId, { type: 'nudge', source: 'detail_page' })])
      .then(() => {}, () => {});
    logComplianceEvent({
      supplierId: doc.supplier_id, documentKey: doc.document_key,
      eventType: 'required', actorSide: 'staff', actorUserId: actorId || null,
      meta: { nudge: true },
    });
  }

  return mail;
}

// Reply without deciding — answering a supplier's question, which was
// previously impossible without also rejecting or requesting info. Changes no
// state: the document stays exactly where it was. Emails them, because a reply
// nobody is told about is not a reply.
export async function replyOnDocument({ doc, supplier, documentLabel, body, actorId }) {
  if (!doc) return { error: 'No document' };
  const text = (body || '').trim();
  if (!text) return { error: 'Nothing to send' };

  await appendAdminMessage(doc.supplier_id, doc.document_key, text, actorId);
  logComplianceEvent({
    supplierId: doc.supplier_id, documentKey: doc.document_key,
    eventType: 'replied', actorSide: 'staff', actorUserId: actorId || null, body: text,
  });
  supabase.from('compliance_audit_log')
    .insert([auditRow('override', doc, actorId, { type: 'replied', source: 'detail_page' })])
    .then(() => {}, () => {});

  return emailSupplier({
    supplierId: supplier?.id,
    supplierName: supplier?.supplier_name,
    emailKind: 'compliance_needs_info',
    actorId,
    buildFor: (u, supplierName) => complianceReplyEmail({
      contactName: u.full_name, supplierName, documentLabel, message: text,
      portalUrl: `${window.location.origin}/portal/compliance`,
    }),
  });
}
